import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
    BaseDataService,
    HttpResponsesHandlersFactory,
    IHttpResponseHandlerSettings
} from "@bbwt/modules/data-service";
import { FileDetails } from "@main/file-storage";
import { Prescription } from "@project/models/prescriptions/prescription";
import { IEpisode } from "@project/models/episodes/episode";
import { SharedFile } from "@mipro/custom-controls/shared-file";
import { PrescriptionStatus } from "@project/models/prescriptions/prescription-status";
import { PrescriptionLine } from "@project/models/prescriptions/prescription-line";
import { NotificationService } from "@main/notification/notification.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { NotificationType } from "@main/notification/notification-type";
import { PrescriptionLinesService } from "@project/prescription-shared/classes/prescription-lines.service";
import { DeliveryAddressType } from "@project/models/prescriptions/delivery-address-type";
import { PrescriptionOfflineStorage } from "./prescription-offline.storage";
import { NoteService } from "@project/note-shared/classes/note.service";
import { HistoricalItem } from "@mipro/classes/historical-item";
import {PrescriptionLineOfflineStorage} from "@project/prescription-shared/classes/prescription-line-offline.storage";

@Injectable({
    providedIn: "root"
})
export class PrescriptionsService extends BaseDataService {
    entityTitle = "Prescriptions";
    url = "api/prescriptions";
    prescriptionLinesService: PrescriptionLinesService;

    constructor(
        http: HttpClient,
        httpHandlersFactory: HttpResponsesHandlersFactory,
        private notificationService: NotificationService,
        private patientService: PatientService,
        private notesService: NoteService
    ) {
        super(http, httpHandlersFactory);
    }

    archive(id: string): Promise<void> {
        return this.handleRequest<void>(this.http.put<void>(`${this.url}/${id}/archive`, id));
    }

    delete(id: string): Promise<void> {
        return this.handleRequest<void>(this.http.put<void>(`${this.url}/${id}/delete`, id));
    }

    cancel(id: string, integrationCode?: string): Promise<void> {
        return this.handleRequest<void>(this.http.put<void>(`${this.url}/${id}/cancel`, integrationCode ? {
            integrationCode
        } : null));
    }

    prescribe(prescription: Prescription, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<Prescription> {
        responseHandlerSettings ??= {};
        responseHandlerSettings.successMessage ??= "Prescription has been prescribed successfully";

        return this.handleRequest<Prescription>(
            this.http.put<Prescription>(`${this.url}/${prescription.id}/prescribe`, prescription),
            this.handlersFactory.getForUpdate(this.entityTitle, responseHandlerSettings)
        );
    }

    async createOfflinePrescriptionFromLine(prescriptionLine: PrescriptionLine): Promise<Prescription> {
        const prescription: Prescription = {
            id: null,
            status: PrescriptionStatus.StoredLocal,
            patientId: prescriptionLine.patientId,
            episodeId: prescriptionLine.episodeId,
            episode: prescriptionLine.episode,
            notes: [],
            dispensingMethod: prescriptionLine.dispensingMethod,
            deliveryAddressType: DeliveryAddressType.HomeAddress,
            sentToEmails: "",
            prescriptionLines: [],
            historical: [],
            prescribedByUserId: "",
            prescribedUserFullName: "",
            prescribedUserRole: "",
            createdByOrganizationId: "",
            createdByOrganization: "",
            notificationId: null,
            report: null,
            lastMergedEpisodeId: null,
            walkInId: null,
            route: prescriptionLine.route
        };
        const notification = await this.notificationService.createOffline({
            id: null,
            title: "Prescription - DRAFT OFFLINE for " + this.patientService.currentPatientPlaceHolder.fullName + " pending upload",
            body: null,
            read: false,
            archived: false,
            created: new Date(),
            type: NotificationType.LocalPrescription,
            redirectTo: "/app/prescriptions/new",
            redirectParams: [
                {
                    name: "patientId",
                    value: this.patientService.currentPatientId.toString()
                },
                {
                    name: "prescriptionId",
                    value: "0"
                }
            ],
            savedOffline: true
        });
        prescription.notificationId = notification.id;
        return PrescriptionOfflineStorage.save(prescription);
    }

    deleteOfflinePrescription(id: string): Prescription {
        const prescription: Prescription = PrescriptionOfflineStorage.remove(id);
        if (!prescription) return;

        this.notificationService.deleteOfflineNotification(prescription.notificationId.toString());
        prescription.notificationId = null;
        PrescriptionLineOfflineStorage.removeLines(prescription.id);
        this.notesService.removeOfflinePrescriptionNotes(id);
        return prescription;
    }

    createPrivateReport(prescription: Prescription, usePreferredPharmacy = false): Promise<FileDetails> {
        return this.handleRequest(
            this.http.post<FileDetails>(
                `${this.url}/${prescription.id}/create-private-report?usePreferredPharmacy=${usePreferredPharmacy}`,
                prescription
            )
        );
    }

    addSentToEmails(prescriptionId: string, sharedFile: SharedFile): Promise<SharedFile> {
        return this.handleRequest(this.http.post<SharedFile>(`${this.url}/${prescriptionId}/add-sent-to-emails-prescription`, sharedFile));
    }

    createReport(patientId: string): Promise<FileDetails> {
        return this.handleRequest(this.http.post<FileDetails>(`${this.url}/create-report/${patientId}`, null));
    }

    getOutstandingPrescriptionId(currentPatientId: string): Promise<string> {
        return this.handleRequest(this.http.get<string>(`${this.url}/outstanding/${currentPatientId}`), this.handlersFactory.getDefault());
    }

    async getOutstandingPrescription(outstandingPrescriptionId: string): Promise<Prescription> {
        const outstandingPrescription: Prescription = PrescriptionOfflineStorage.get(outstandingPrescriptionId);
        return (
            outstandingPrescription ??
            (await this.handleRequest(
                this.http.get<Prescription>(`${this.url}/${outstandingPrescriptionId}/outstanding`),
                this.handlersFactory.getDefault()
            ))
        );
    }

    async getOutstandingPrescribePrescription(outstandingPrescriptionId: string): Promise<Prescription> {
        const outstandingPrescription: Prescription = PrescriptionOfflineStorage.get(outstandingPrescriptionId);
        return outstandingPrescription
            ?? await this.handleRequest(
                this.http.get<Prescription>(`${this.url}/${outstandingPrescriptionId}/outstanding/prescribe`),
                this.handlersFactory.getDefault()
            );
    }

    async deleteOutstandingPrescription(prescriptionId: string): Promise<any> {
        return this.deleteOfflinePrescription(prescriptionId)
            ?? await this.handleRequest(
                this.http.delete(`${this.url}/${prescriptionId}/outstanding`),
                this.handlersFactory.getForDelete("Draft Prescription")
        );
    }

    async updateDraftPrescriptionEpisode(
        prescriptionId: string,
        episode: IEpisode,
        responseHandlerSettings?: IHttpResponseHandlerSettings
    ): Promise<Prescription> {
        responseHandlerSettings ??= {};
        responseHandlerSettings.successMessage ??= "Prescription's episode updated successfully";

        const prescription = PrescriptionOfflineStorage.get(prescriptionId);
        if (prescription) {
            this.prescriptionLinesService.updateLocalPrescriptionsLinesEpisode(prescription.id, episode);
            return PrescriptionOfflineStorage.get(prescriptionId);
        } else {
            return this.handleRequest(
                this.http.put<Prescription>(`${this.url}/${prescriptionId}/outstanding/episode`, episode),
                this.handlersFactory.getForUpdate(this.entityTitle, responseHandlerSettings)
            );
        }
    }

    createDraftPrescriptionFromId(prescriptionId: string) {
        return this.handleRequest(
            this.http.post<Prescription>(`${this.url}/${prescriptionId}/outstanding`, null),
            this.handlersFactory.getForCreate("Draft Prescription", {successMessage: "Draft Prescription created successfully"})
        );
    }

    createDraftPrescriptionFromLineId(prescriptionLineId: string): Promise<Prescription> {
        return this.handleRequest(
            this.http.post<Prescription>(`${this.url}/from-line/${prescriptionLineId}/outstanding`, null),
            this.handlersFactory.getForCreate(
                "Draft Prescription",
                {successMessage: "Draft Prescription created successfully"}
            )
        );
    }

    updatePrivateStatus(masterProfileId: string, status: boolean): Promise<void> {
        return this.handleRequest<void>(this.http.put<void>(`${this.url}/${masterProfileId}/update-private-status/${status}`, null));
    }

    isJsReportFunctioning(): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-jsreport-functioning`)
        );
    }

    getHistoricalItems(prescriptionId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical-items/${prescriptionId}`),
            this.handlersFactory.getForReadAll());
    }
}
